import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress
} from "@mui/material";
import { useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import { GridModel } from "features/api";

interface SelectionModalProps<T> {
  open: boolean;
  onClose: () => void;
  onSelect: (item: T) => void;
  title: string;
  searchPlaceholder: string;
  items: T[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  totalCount: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onSearchChange: (query: string) => void;
  renderItem: (item: T) => {
    primary: string;
    secondary?: string;
  };
  additionalFilters?: React.ReactNode;
}

const SelectionModal = <T,>({
  open,
  onClose,
  onSelect,
  title,
  searchPlaceholder,
  items,
  isLoading,
  isFetching,
  totalCount,
  pageSize,
  currentPage,
  onPageChange,
  onSearchChange,
  renderItem,
  additionalFilters
}: SelectionModalProps<T>) => {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearchChange(value);
  };

  const handlePageChange = (event: any, value: number) => {
    onPageChange(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, padding: 1 }}>
          {additionalFilters}
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <List>
              {items?.map((item, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton onClick={() => onSelect(item)}>
                    <ListItemText
                      primary={renderItem(item).primary}
                      secondary={
                        renderItem(item).secondary && (
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {renderItem(item).secondary}
                            </Typography>
                          </Box>
                        )
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {totalCount > pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, position: 'relative' }}>
                {isFetching && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px'
                    }}
                  />
                )}
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  disabled={isFetching}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectionModal; 
